import React from 'react';
import {
  Box, Container, Typography, Stack, Paper, Avatar, Grid,
  Chip, Button,
} from '@mui/material';
import { BookmarkBorder, School, Route, Info } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePaths } from '../../context/PathsContext';
import { useCatalog } from '../../context/CatalogContext';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { paths } = usePaths();
  const { modules, learningPaths } = useCatalog();

  const totalPathModules = paths.reduce((acc, p) => acc + (p.modules || []).length, 0);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Profile header */}
      <Paper sx={{ p: 4, borderRadius: '16px', border: '1px solid #e5e7eb', mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
          <Avatar sx={{ width: 80, height: 80, fontSize: '2rem', backgroundColor: '#0078d4', flexShrink: 0 }}>
            U
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={800}>Learning Explorer</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Using Microsoft Learn Catalog to build expertise
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Chip icon={<BookmarkBorder />} label={`${paths.length} paths`} size="small" sx={{ backgroundColor: '#eff6ff', color: '#1d4ed8' }} />
              <Chip icon={<School />} label={`${totalPathModules} modules saved`} size="small" sx={{ backgroundColor: '#f0fdf4', color: '#166534' }} />
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatCard icon={<BookmarkBorder />} value={paths.length} label="Custom paths" color="#0078d4" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard icon={<School />} value={modules.length.toLocaleString()} label="Modules in catalog" color="#7c3aed" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard icon={<Route />} value={learningPaths.length.toLocaleString()} label="Learning paths in catalog" color="#059669" />
        </Grid>
      </Grid>

      {/* My paths summary */}
      <Paper sx={{ p: 3, borderRadius: '14px', border: '1px solid #e5e7eb', mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>My Learning Paths</Typography>
          <Button size="small" onClick={() => navigate('/create-path')} sx={{ fontWeight: 600 }}>
            {paths.length === 0 ? 'Create first' : 'Manage all'}
          </Button>
        </Stack>

        {paths.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>
            <BookmarkBorder sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body2">No custom paths yet. Create one to get started.</Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {paths.slice(0, 5).map((path) => (
              <Box
                key={path.id}
                sx={{
                  p: 2, borderRadius: '10px', backgroundColor: '#f9fafb',
                  border: '1px solid #f3f4f6', cursor: 'pointer',
                  '&:hover': { backgroundColor: '#f3f4f6' },
                }}
                onClick={() => navigate('/create-path')}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{path.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(path.modules || []).length} module{(path.modules || []).length !== 1 ? 's' : ''} ·
                      Updated {new Date(path.updatedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Chip label={(path.modules || []).length} size="small" sx={{ backgroundColor: '#eff6ff', color: '#1d4ed8', fontWeight: 700 }} />
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {/* About */}
      <Paper sx={{ p: 3, borderRadius: '14px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Info sx={{ color: 'text.disabled', mt: 0.25 }} />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>About this app</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              LearnFinder connects to the{' '}
              <Box component="a" href="https://learn.microsoft.com/api/catalog/" target="_blank" rel="noopener noreferrer"
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                Microsoft Learn Catalog API
              </Box>
              {' '}to browse and search all published training content. Custom learning paths are stored
              locally in your browser and never sent to any server.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
}

function StatCard({ icon, value, label, color }) {
  return (
    <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, color }}>
        {React.cloneElement(icon, { sx: { fontSize: 28, color } })}
      </Box>
      <Typography variant="h5" fontWeight={800}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  );
}
